import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";

/* Pumps are not treated as "real" assets, they have no own geometry, organisation,
   image_url, and will be serialized as part of pumpstations. */

export const pumpListItemSchema = baseAssetWithTimeseriesSchema
  .omit({ geometry: true, organisation: true, image_url: true })
  .extend({
    name: z.string(),
    type: z.string(),
    serial: z.number().int().nullable(),
    allowed_flow_direction: z.string().nullable() /* backwards | forwards | both | closed */,
    capacity: z.number().nullable(),
    start_level: z.number().nullable(),
    stop_level: z.number().nullable(),
    start_level_delivery_side: z.number().nullable(),
    stop_level_delivery_side: z.number().nullable(),
    reduction_factor_no_levels: z.number().nullable(),
    reduction_factor: z.number().nullable(),
    characteristics: z.string().nullable(),
  });

export type PumpListItem = z.infer<typeof pumpListItemSchema>;

export const pumpListSchema = paginatedSchema(pumpListItemSchema);
export type PumpList = z.infer<typeof pumpListSchema>;

export const pumpSchema = pumpListItemSchema;
export type Pump = PumpListItem;

export function usePumps(params = {}) {
  const url = combineUrlAndParams("/api/v4/pumps/", params);
  return useParsedQuery<PumpList>(
    ["lizard-api-v4", "pumps", url],
    () => fetchWithError(url),
    (json: unknown) => pumpListSchema.parse(json)
  );
}

export function usePump(id: number) {
  return useParsedQuery<Pump>(
    ["lizard-api-v4", "pumps", id],
    () => fetchWithError(`/api/v4/pumps/${id}/`),
    (json: unknown) => pumpSchema.parse(json)
  );
}

export function useMultiplePumps(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "pumps", id],
        fetchFunction: () => fetchWithError(`/api/v4/pumps/${id}/`),
        parseFunction: (json: unknown) => pumpSchema.parse(json),
      };
    })
  );
}
